<script setup>
import { computed, ref, reactive, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useRoute, useRouter } from 'vue-router';
import CompanyAPI from 'dashboard/api/companies';

const props = defineProps({
  contacts: { type: Array, default: () => [] },
  selectedContactIds: { type: Array, default: () => [] },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
});

const emit = defineEmits(['toggleContact', 'toggleAll', 'update:sort']);

const route = useRoute();
const router = useRouter();

const viewContactDetails = id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };
  router.push({ name, params, query: route.query });
};

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const contactFieldDefs = computed(() =>
  getters['contactFieldDefinitions/getEnabled'].value || []
);

// ── Column definitions ────────────────────────────────────────────────────────
const SYSTEM_COLUMNS = [
  { key: 'name', label: 'Name', fieldType: 'text', sortKey: 'name', always: true, isSystem: true },
  { key: 'email', label: 'Email', fieldType: 'text', sortKey: 'email', isSystem: true },
  { key: 'phoneNumber', label: 'Phone', fieldType: 'text', sortKey: 'phone_number', isSystem: true },
  { key: 'city', label: 'City', fieldType: 'text', isSystem: true, isAdditional: true, additionalKey: 'city' },
  { key: 'countryCode', label: 'Country', fieldType: 'text', isSystem: true, isAdditional: true, additionalKey: 'countryCode' },
  { key: 'createdAt', label: 'Created', fieldType: 'date', sortKey: 'created_at', readOnly: true, isSystem: true },
  { key: 'lastActivityAt', label: 'Last Activity', fieldType: 'date', sortKey: 'last_activity_at', readOnly: true, isSystem: true },
];

const DEFAULT_VISIBLE = new Set(['name', 'email', 'phoneNumber', 'createdAt']);

const STORAGE_KEY = 'contacts_table_visible_columns';

const loadVisibleKeys = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return new Set(JSON.parse(saved));
  } catch {}
  return new Set(DEFAULT_VISIBLE);
};

const visibleKeys = ref(loadVisibleKeys());

const saveVisibleKeys = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(visibleKeys.value)));
  } catch {}
};

const allColumns = computed(() => {
  const customCols = contactFieldDefs.value.map(fd => ({
    key: fd.field_key,
    label: fd.label,
    fieldType: fd.field_type,
    options: fd.options || [],
    isCustom: true,
    fieldConfig: fd.field_config || {},
  }));
  return [...SYSTEM_COLUMNS, ...customCols];
});

const visibleColumns = computed(() =>
  allColumns.value.filter(c => c.always || visibleKeys.value.has(c.key))
);

// ── Customize panel ───────────────────────────────────────────────────────────
const showCustomize = ref(false);

const toggleCustomize = () => { showCustomize.value = !showCustomize.value; };
const closeCustomize = () => { showCustomize.value = false; };

const toggleColumn = key => {
  if (visibleKeys.value.has(key)) {
    visibleKeys.value.delete(key);
  } else {
    visibleKeys.value.add(key);
  }
  visibleKeys.value = new Set(visibleKeys.value);
  saveVisibleKeys();
};

// ── Cell value ────────────────────────────────────────────────────────────────
const formatDate = ts => {
  if (!ts) return '';
  const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const getCellValue = (contact, col) => {
  if (col.isCustom) {
    const v = contact.extraFields?.[col.key] ?? '';
    if (!v && col.fieldType === 'group') return contact.companyDomain || '';
    return v;
  }
  if (col.isAdditional) return contact.additionalAttributes?.[col.additionalKey] ?? '';
  if (col.fieldType === 'date') return formatDate(contact[col.key]);
  return contact[col.key] ?? '';
};

const getEditableValue = (contact, col) => {
  if (col.isCustom) return contact.extraFields?.[col.key] ?? '';
  if (col.isAdditional) return contact.additionalAttributes?.[col.additionalKey] ?? '';
  return contact[col.key] ?? '';
};

// ── Inline editing ────────────────────────────────────────────────────────────
const editState = reactive({ contactId: null, colKey: null });
const editValue = ref('');
const editInputRef = ref(null);

const isEditing = (contact, col) =>
  editState.contactId === contact.id && editState.colKey === col.key;

// ── Company search (for group-type custom fields) ─────────────────────────────
const companySearchState = reactive({ contactId: null, colKey: null });
const companySearchQuery = ref('');
const companySearchResults = ref([]);
const isSearchingCompanies = ref(false);
const companySearchInputRef = ref(null);
let companySearchTimer = null;

const isCompanySearching = (contact, col) =>
  companySearchState.contactId === contact.id && companySearchState.colKey === col.key;

const openCompanySearch = (contact, col) => {
  companySearchState.contactId = contact.id;
  companySearchState.colKey = col.key;
  companySearchQuery.value = getCellValue(contact, col) || '';
  companySearchResults.value = [];
  if (companySearchQuery.value) searchCompanies(companySearchQuery.value);
  nextTick(() => companySearchInputRef.value?.focus?.());
};

const searchCompanies = query => {
  if (!query) { companySearchResults.value = []; return; }
  clearTimeout(companySearchTimer);
  companySearchTimer = setTimeout(async () => {
    isSearchingCompanies.value = true;
    try {
      const { data } = await CompanyAPI.search(query);
      companySearchResults.value = data.payload || [];
    } catch {
      companySearchResults.value = [];
    } finally {
      isSearchingCompanies.value = false;
    }
  }, 300);
};

const selectCompany = async (contact, company) => {
  const contactId = companySearchState.contactId;
  companySearchState.contactId = null;
  companySearchState.colKey = null;
  companySearchQuery.value = '';
  companySearchResults.value = [];
  try {
    await store.dispatch('contacts/update', { id: contactId, companyId: company.id });
  } catch {
    useAlert('Failed to update company');
  }
};

const cancelCompanySearch = () => {
  companySearchState.contactId = null;
  companySearchState.colKey = null;
  companySearchQuery.value = '';
  companySearchResults.value = [];
};

const startEdit = (contact, col) => {
  if (col.readOnly || col.fieldType === 'date') return;
  if (col.isCustom && col.fieldType === 'checkbox') {
    toggleCheckbox(contact, col);
    return;
  }
  if (col.isCustom && col.fieldType === 'group') {
    openCompanySearch(contact, col);
    return;
  }
  editState.contactId = contact.id;
  editState.colKey = col.key;
  editValue.value = String(getEditableValue(contact, col));
  nextTick(() => editInputRef.value?.focus?.());
};

const cancelEdit = () => {
  editState.contactId = null;
  editState.colKey = null;
  editValue.value = '';
};

const saveEdit = async () => {
  if (!editState.contactId || !editState.colKey) return;
  const contactId = editState.contactId;
  const colKey = editState.colKey;
  const value = editValue.value;
  cancelEdit();

  const contact = props.contacts.find(c => c.id === contactId);
  if (!contact) return;
  const col = allColumns.value.find(c => c.key === colKey);
  if (!col) return;

  try {
    if (col.isCustom) {
      await store.dispatch('contacts/update', {
        id: contactId,
        extraFields: { ...(contact.extraFields || {}), [col.key]: value },
      });
    } else if (col.isAdditional) {
      await store.dispatch('contacts/update', {
        id: contactId,
        additionalAttributes: { ...(contact.additionalAttributes || {}), [col.additionalKey]: value },
      });
    } else {
      await store.dispatch('contacts/update', { id: contactId, [col.key]: value });
    }
  } catch {
    useAlert('Failed to update contact');
  }
};

const toggleCheckbox = async (contact, col) => {
  const current = contact.extraFields?.[col.key];
  const next = !current;
  try {
    await store.dispatch('contacts/update', {
      id: contact.id,
      extraFields: { ...(contact.extraFields || {}), [col.key]: next },
    });
  } catch {
    useAlert('Failed to update contact');
  }
};

// ── Selection ─────────────────────────────────────────────────────────────────
const selectedSet = computed(() => new Set(props.selectedContactIds));

const isSelected = contact => selectedSet.value.has(contact.id);

const allSelected = computed(() =>
  props.contacts.length > 0 && props.contacts.every(c => selectedSet.value.has(c.id))
);

const someSelected = computed(() =>
  props.contacts.some(c => selectedSet.value.has(c.id)) && !allSelected.value
);

const toggleSelect = (contact, value) => {
  emit('toggleContact', { id: contact.id, value });
};

const toggleSelectAll = checked => {
  emit('toggleAll', { ids: props.contacts.map(c => c.id), value: checked });
};

// ── Sorting ───────────────────────────────────────────────────────────────────
const handleHeaderClick = col => {
  if (!col.sortKey) return;
  if (props.activeSort === col.sortKey) {
    emit('update:sort', {
      sort: col.sortKey,
      order: props.activeOrdering === '-' ? '' : '-',
    });
  } else {
    emit('update:sort', { sort: col.sortKey, order: '' });
  }
};

// ── Column resize ─────────────────────────────────────────────────────────────
const COL_WIDTHS_KEY = 'contacts_col_widths';
const colWidths = ref({});

const loadColWidths = () => {
  try {
    const saved = localStorage.getItem(COL_WIDTHS_KEY);
    if (saved) colWidths.value = JSON.parse(saved);
  } catch {}
};

const saveColWidths = () => {
  try {
    localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(colWidths.value));
  } catch {}
};

let resizeState = null;

const startColResize = (e, key) => {
  e.preventDefault();
  e.stopPropagation();
  const th = e.currentTarget.parentElement;
  const startWidth = colWidths.value[key] || th.offsetWidth;
  resizeState = { key, startX: e.clientX, startWidth };

  const onMove = ev => {
    if (!resizeState) return;
    const newWidth = Math.max(60, resizeState.startWidth + (ev.clientX - resizeState.startX));
    colWidths.value = { ...colWidths.value, [resizeState.key]: newWidth };
  };

  const onUp = () => {
    saveColWidths();
    resizeState = null;
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
  };

  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
};

const colStyle = key => {
  const w = colWidths.value[key];
  return w ? { width: `${w}px`, minWidth: `${w}px` } : {};
};

loadColWidths();
</script>

<template>
  <div class="w-full flex flex-col">
    <!-- Customize table button -->
    <div class="flex items-center justify-end px-2 py-2 border-b border-n-weak">
      <div class="relative">
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-11 rounded border border-n-weak hover:bg-n-alpha-1 transition-colors"
          @click="toggleCustomize"
        >
          <span class="i-lucide-settings-2 size-3.5" />
          Customize table
        </button>
        <!-- Column visibility dropdown -->
        <div
          v-if="showCustomize"
          v-click-outside="closeCustomize"
          class="absolute right-0 top-9 z-30 min-w-[180px] bg-n-solid-2 border border-n-weak rounded-lg shadow-lg p-2 flex flex-col gap-0.5"
        >
          <template v-for="col in allColumns" :key="col.key">
            <label
              v-if="!col.always"
              class="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-n-alpha-1 cursor-pointer text-sm text-n-slate-12"
            >
              <input
                type="checkbox"
                :checked="visibleKeys.has(col.key)"
                class="w-3.5 h-3.5 accent-woot-500"
                @change="toggleColumn(col.key)"
              />
              {{ col.label }}
            </label>
          </template>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="w-full text-sm border-collapse">
        <thead>
          <tr class="border-b border-n-weak bg-n-surface-1">
            <!-- Checkbox header -->
            <th class="px-3 py-2.5 w-10 text-left">
              <input
                type="checkbox"
                :checked="allSelected"
                :indeterminate="someSelected"
                class="w-3.5 h-3.5 accent-woot-500 cursor-pointer"
                @change="e => toggleSelectAll(e.target.checked)"
              />
            </th>
            <!-- Column headers -->
            <th
              v-for="col in visibleColumns"
              :key="col.key"
              class="relative px-4 py-2.5 text-left text-xs font-medium text-n-slate-9 whitespace-nowrap select-none"
              :class="col.sortKey ? 'cursor-pointer hover:text-n-slate-12' : ''"
              :style="colStyle(col.key)"
              @click="handleHeaderClick(col)"
            >
              <div class="flex items-center gap-1">
                {{ col.label }}
                <template v-if="col.sortKey">
                  <span
                    v-if="activeSort === col.sortKey && activeOrdering === ''"
                    class="i-lucide-arrow-up size-3"
                  />
                  <span
                    v-else-if="activeSort === col.sortKey && activeOrdering === '-'"
                    class="i-lucide-arrow-down size-3"
                  />
                  <span v-else class="i-lucide-arrow-up-down size-3 opacity-30" />
                </template>
              </div>
              <div class="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-woot-500/40 z-10" @mousedown.stop="startColResize($event, col.key)" />
            </th>
            <!-- Actions header -->
            <th class="px-4 py-2.5 w-32 bg-n-surface-1" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="contact in contacts"
            :key="contact.id"
            class="border-b border-n-weak hover:bg-n-alpha-1 transition-colors group"
            :class="{ 'bg-n-alpha-2': isSelected(contact) }"
          >
            <!-- Checkbox cell -->
            <td class="px-3 py-2.5 w-10">
              <input
                type="checkbox"
                :checked="isSelected(contact)"
                class="w-3.5 h-3.5 accent-woot-500 cursor-pointer"
                @change="e => toggleSelect(contact, e.target.checked)"
              />
            </td>
            <!-- Data cells -->
            <td
              v-for="col in visibleColumns"
              :key="col.key"
              class="px-4 py-2.5 max-w-[200px] text-n-slate-11"
              :class="[
                !col.readOnly && col.fieldType !== 'date'
                  ? 'hover:bg-n-alpha-2 cursor-text'
                  : '',
                col.key === 'name' ? 'font-medium text-n-slate-12' : '',
              ]"
              @click="startEdit(contact, col)"
            >
              <!-- Editing state -->
              <template v-if="isEditing(contact, col)">
                <!-- List (select) -->
                <select
                  v-if="col.fieldType === 'list'"
                  ref="editInputRef"
                  v-model="editValue"
                  class="w-full px-1 py-0.5 text-sm border border-woot-400 rounded bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
                  @change="saveEdit"
                  @keydown.esc.prevent="cancelEdit"
                  @blur="saveEdit"
                  @click.stop
                >
                  <option value="">— select —</option>
                  <option v-for="opt in col.options" :key="opt" :value="opt">{{ opt }}</option>
                </select>
                <!-- Other inputs -->
                <input
                  v-else
                  ref="editInputRef"
                  v-model="editValue"
                  :type="col.fieldType === 'number' ? 'number' : 'text'"
                  class="w-full px-1 py-0.5 text-sm border border-woot-400 rounded bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
                  @keydown.enter.prevent="saveEdit"
                  @keydown.esc.prevent="cancelEdit"
                  @blur="saveEdit"
                  @click.stop
                />
              </template>
              <!-- Company search state (group type) -->
              <template v-else-if="isCompanySearching(contact, col)">
                <div class="relative" @click.stop>
                  <input
                    ref="companySearchInputRef"
                    v-model="companySearchQuery"
                    type="text"
                    placeholder="Search company..."
                    class="w-full px-1 py-0.5 text-sm border border-woot-400 rounded bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
                    @input="searchCompanies(companySearchQuery)"
                    @keydown.esc.prevent="cancelCompanySearch"
                    @blur="cancelCompanySearch"
                  />
                  <div
                    v-if="companySearchResults.length"
                    class="absolute left-0 top-6 z-50 min-w-[200px] bg-n-solid-2 border border-n-weak rounded shadow-lg overflow-auto max-h-40"
                  >
                    <button
                      v-for="company in companySearchResults"
                      :key="company.id"
                      class="w-full text-left px-3 py-1.5 text-sm hover:bg-n-alpha-2 text-n-slate-12"
                      @mousedown.prevent="selectCompany(contact, company)"
                    >
                      {{ company.name }}
                    </button>
                  </div>
                  <div
                    v-else-if="isSearchingCompanies"
                    class="absolute left-0 top-6 z-50 min-w-[200px] bg-n-solid-2 border border-n-weak rounded shadow-lg px-3 py-2 text-xs text-n-slate-9"
                  >
                    Searching...
                  </div>
                </div>
              </template>
              <!-- Display state -->
              <template v-else>
                <!-- Checkbox field -->
                <template v-if="col.isCustom && col.fieldType === 'checkbox'">
                  <span
                    class="inline-flex items-center justify-center w-4 h-4 rounded"
                    :class="getCellValue(contact, col) ? 'bg-woot-500 text-white' : 'border border-n-weak'"
                  >
                    <span v-if="getCellValue(contact, col)" class="i-lucide-check size-3" />
                  </span>
                </template>
                <!-- Link field -->
                <template v-else-if="col.isCustom && col.fieldType === 'link'">
                  <a
                    v-if="getCellValue(contact, col)"
                    :href="getCellValue(contact, col)"
                    target="_blank"
                    rel="noopener"
                    class="text-woot-500 hover:underline truncate block"
                    @click.stop
                  >{{ getCellValue(contact, col) }}</a>
                  <span v-else class="text-n-slate-7 text-xs">+ Click to add</span>
                </template>
                <!-- Default text -->
                <template v-else>
                  <span v-if="getCellValue(contact, col)" class="block truncate">
                    {{ getCellValue(contact, col) }}
                  </span>
                  <span v-else class="text-n-slate-7 text-xs">
                    {{ col.readOnly ? '—' : '+ Click to add' }}
                  </span>
                </template>
              </template>
            </td>
            <!-- View all details button -->
            <td class="px-4 py-2.5 w-32" @click.stop>
              <button
                class="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-woot-500 border border-woot-300 rounded hover:bg-woot-50 dark:hover:bg-woot-900/20 transition-colors whitespace-nowrap opacity-0 group-hover:opacity-100"
                @click="viewContactDetails(contact.id)"
              >
                <span class="i-lucide-external-link size-3" />
                View details
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
